import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { billingService } from '../services/api';
import type { BillingInfo } from '../types/api';
import { CHECKOUT_RETURN_PARAMS } from '../utils/checkout';
import { useToast } from './Toast';

// Back from the website's checkout page (PLAN-8, PLAN-17): Paddle sends the browser to the page the user came from with
// ?checkout=success&plan=…(&class=…). Paddle's webhook sets the plan, possibly a few seconds later: say the payment
// arrived, wait (up to ~30 s) for the plan to show in /api/billing/me, then reload so every view shows it.

const ACTIVATED_KEY = 'webots-checkout-activated';

function planIsActive(info: BillingInfo, plan: string | null, classId: number | null): boolean {
  if (plan === 'student') return info.plan === 'student';
  if (plan === 'class') return !!info.classes?.some((cls) => cls.id === classId && cls.plan === 'class');
  return false;
}

const CheckoutReturn: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const handled = useRef(false);
  // The wait below outlives the effect (removing the parameters re-runs it): it stops only when this unmounts.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // After the reload below: say the plan is on.
  useEffect(() => {
    let activated: string | null = null;
    try {
      activated = sessionStorage.getItem(ACTIVATED_KEY);
      sessionStorage.removeItem(ACTIVATED_KEY);
    } catch {
      // storage blocked: no second message
    }
    if (activated) toast.success(activated === 'class' ? 'The Class plan is active for your class.' : 'Your Student plan is active.');
  }, [toast]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Logged out, the page sends the user to log in first and keeps the parameters for when they are back.
    if (handled.current || !user || params.get('checkout') !== 'success') return;
    handled.current = true;
    const plan = params.get('plan');
    const classId = Number(params.get('class')) || null;
    // Once only: a reload or a bookmark of this URL must not say it again.
    CHECKOUT_RETURN_PARAMS.forEach((name) => params.delete(name));
    const query = params.toString();
    navigate({ pathname: location.pathname, search: query ? `?${query}` : '', hash: location.hash }, { replace: true });
    toast.success('Payment received: your plan will be active in a moment.', { durationMs: 10000 });
    if (plan !== 'student' && plan !== 'class') return;
    void (async () => {
      for (let attempt = 0; attempt < 15 && mounted.current; attempt += 1) {
        try {
          if (planIsActive(await billingService.me(), plan, classId)) {
            try {
              sessionStorage.setItem(ACTIVATED_KEY, plan);
            } catch {
              // storage blocked: the reload alone shows the plan
            }
            window.location.reload();
            return;
          }
        } catch {
          // keep waiting
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (mounted.current) {
        toast.info(`The payment went through, but your plan isn't showing yet. Reload the page in a minute; if it still isn't there, write to contact@webots.academy.`, { durationMs: 15000 });
      }
    })();
  }, [user, location.pathname, location.search, location.hash, navigate, toast]);

  return null;
};

export default CheckoutReturn;
